import { FeatureGate } from '@orn/global/featureGates/featureGates.js';
import { useBreakpoint } from '@orn/shared-web/hooks/useBreakpoint';
import * as React from 'react';

import { useTransitionedPresence, type ITransitionedPresence } from '../hooks/transition/useTransitionedPresence';
import { useFeatureGate } from '../hooks/useFeatureGates';
import { SIDE_PANEL_TRANSITION_MS } from '../utils/sidePanelMotion';

import {
  INITIAL_ALERTS_PRESENTATION,
  reduceAlertsPresentation,
  type AlertsPresentationAction,
  type IAlertsPresentationState,
} from './alertsPresentation';

import type { AlertsCenterPresentation } from '../utils/alert/alertsCenterCard';

/** What the side panel seats. The Notification Center is a layer of the assistant pane, not a second occupant. */
export type SidePanelContent = 'assistant';

export { SIDE_PANEL_TRANSITION_MS } from '../utils/sidePanelMotion';

/**
 * The viewport breakpoint at which `EntityPaneBody` switches from one to two
 * columns. Shifts to `2xl` while the side panel occupies horizontal space so
 * the narrowed content track does not try to host two columns until there is
 * genuinely room.
 */
export type ContentColumnsBreakpoint = 'xl' | '2xl';

/** An alert handed to the assistant thread, waiting for the pane to open it. */
export interface IPendingAlertExpand {
  alertId: string;
  /**
   * Whether the row was still unread when it was clicked. Carried rather than
   * re-read later because the same click marks the alert read, so by the time
   * the reply renders the stored status no longer answers "was this new to the
   * user" — see `AssistantPaneView`.
   */
  wasUnread: boolean;
}

/** The panel's own state. */
export interface ISidePanelState {
  isOpen: boolean;
  alerts: IAlertsPresentationState;
}

/**
 * What the shell and the chrome can do to the panel. The alerts actions are
 * the card's own reducer; the two that need the column as well (`showAlerts`,
 * `toggleAlertsCenter`) are composed here, which is why the column and the card
 * share one reducer rather than two state slots.
 */
export type SidePanelAction =
  | { type: 'toggleAssistant' }
  | { type: 'showAssistant' }
  | { type: 'showAlerts' }
  | { type: 'toggleAlertsCenter' }
  | { type: 'alerts'; action: AlertsPresentationAction };

interface ISidePanelContextValue {
  isOpen: boolean;
  isAlertsEnabled: boolean;
  /**
   * What the panel would render, or `null` when nothing is showable. `HomeScene`
   * still owns the `xl+` check — below that breakpoint there is no column.
   */
  visible: SidePanelContent | null;
  /**
   * The `xl+` column's presentational presence: `renderValue` retains the
   * occupant through the ~{@link SIDE_PANEL_TRANSITION_MS} exit so `HomeScene`
   * can play the collapse before unmounting, and `stage` names where the width
   * transition is. Lives here rather than in the scene because
   * {@link ContentColumnsBreakpoint} must flip with the column the user *sees*:
   * keying it on `visible` re-lays panes out at animation start, a visible
   * one-frame snap before the width has moved.
   */
  panelPresence: ITransitionedPresence<SidePanelContent>;
  /** `2xl` while the panel consumes content width, else `xl`. */
  columnsBreakpoint: ContentColumnsBreakpoint;
  /** The nav pill: opens the column, or closes it when it is open. */
  toggleAssistant: () => void;
  /** Opens the column on the assistant thread, leaving the card where it is. */
  showAssistant: () => void;
  /**
   * An `/alerts` deep link, and the bell below `xl`: opens the column and the
   * full center. Idempotent — a deep link while the center is already full
   * keeps it full and keeps the presentation it will return to.
   */
  showAlerts: () => void;
  /** Which of its three presentations the Notification Center card is in. */
  alertsPresentation: AlertsCenterPresentation;
  /** The pill was clicked. */
  expandAlertsCenter: () => void;
  /** The chevron on either card: down to the pill. */
  collapseAlertsCenter: () => void;
  /** The truncated card's "View all". */
  viewAllAlertsCenter: () => void;
  /** Open the full center, remembering which composer presentation it came from. */
  openAlertsCenter: () => void;
  /** Escape, or the bell pressed again: back to the presentation the full center came from. */
  closeAlertsCenter: () => void;
  /** The bell at `xl+`: closes the full center when it is showing, otherwise opens the column onto it. */
  toggleAlertsCenter: () => void;
  /** An alert entered the thread — from a card row, or a record page's banner. */
  noteAlertExpanded: () => void;
  /** The user sent a message, from the composer or a suggestion chip. */
  noteUserSent: () => void;
  /** A row with a fix of its own ran that fix instead of opening in the thread. */
  noteRowFixRan: () => void;
  /**
   * The alert the assistant has been asked to open but has not opened yet, or
   * `null` when nothing is waiting. Lives here rather than on a surface
   * because the surface that asks (a record page's banner) is not the pane
   * that has to act, and below `xl` that pane may not be mounted yet.
   */
  pendingAlertExpand: IPendingAlertExpand | null;
  /**
   * Hand an alert to the assistant thread: opens the column and records which
   * alert to open. Below `xl` the caller still has to navigate — see
   * `useOpenAlertInAssistant`, which is what surfaces should call.
   */
  openAlertInAssistant: (alertId: string, wasUnread: boolean) => void;
  /** Clears the pending alert, once the pane has issued its expand. */
  consumePendingExpand: () => void;
}

/**
 * Default reflects "no side panel" — gates off, today's column behavior. Lets
 * `EntityPaneBody` (and its pane Storybook stories) consume
 * {@link useContentColumnsBreakpoint} without being wrapped in the provider.
 */
const DEFAULT_VALUE: ISidePanelContextValue = {
  isOpen: false,
  isAlertsEnabled: false,
  visible: null,
  panelPresence: { renderValue: null, stage: 'open', onTransitionEnd: () => {} },
  columnsBreakpoint: 'xl',
  toggleAssistant: () => {},
  showAssistant: () => {},
  showAlerts: () => {},
  alertsPresentation: INITIAL_ALERTS_PRESENTATION.presentation,
  expandAlertsCenter: () => {},
  collapseAlertsCenter: () => {},
  viewAllAlertsCenter: () => {},
  openAlertsCenter: () => {},
  closeAlertsCenter: () => {},
  toggleAlertsCenter: () => {},
  noteAlertExpanded: () => {},
  noteUserSent: () => {},
  noteRowFixRan: () => {},
  pendingAlertExpand: null,
  openAlertInAssistant: () => {},
  consumePendingExpand: () => {},
};

const SidePanelContext = React.createContext<ISidePanelContextValue>(DEFAULT_VALUE);

/** A session starts with the column open on the assistant and the card truncated. */
export const INITIAL_SIDE_PANEL_STATE: ISidePanelState = { isOpen: true, alerts: INITIAL_ALERTS_PRESENTATION };

/**
 * The column and the card together. Pure, so the composed moves — the bell
 * toggle especially, which reads both — are unit tests rather than provider
 * choreography. Returns the same object when nothing changes.
 */
export const reduceSidePanel = (state: ISidePanelState, action: SidePanelAction): ISidePanelState => {
  switch (action.type) {
    case 'toggleAssistant':
      return { ...state, isOpen: !state.isOpen };

    case 'showAssistant':
      return state.isOpen ? state : { ...state, isOpen: true };

    case 'showAlerts':
      return openOnto(state, reduceAlertsPresentation(state.alerts, 'open'));

    case 'toggleAlertsCenter':
      // "Full" only counts while the column shows it: a stale full presentation
      // behind a closed column must open, not close.
      if (state.isOpen && state.alerts.presentation === 'full') {
        return withAlerts(state, reduceAlertsPresentation(state.alerts, 'close'));
      }
      return openOnto(state, reduceAlertsPresentation(state.alerts, 'open'));

    case 'alerts':
      return withAlerts(state, reduceAlertsPresentation(state.alerts, action.action));

    // no default
  }
};

const withAlerts = (state: ISidePanelState, alerts: IAlertsPresentationState): ISidePanelState =>
  alerts === state.alerts ? state : { ...state, alerts };

const openOnto = (state: ISidePanelState, alerts: IAlertsPresentationState): ISidePanelState =>
  state.isOpen && alerts === state.alerts ? state : { isOpen: true, alerts };

/** `2xl` while the panel occupies content width, else `xl`. */
export const deriveContentColumnsBreakpoint = (hasVisiblePanel: boolean): ContentColumnsBreakpoint =>
  hasVisiblePanel ? '2xl' : 'xl';

interface ISidePanelProviderProps {
  children: React.ReactNode;
}

/**
 * Holds the cross-cutting state for the assistant's side panel: whether the
 * `xl+` column is open, which of its three presentations the Notification
 * Center card is in (the card is a layer of the assistant pane at every width,
 * so its presentation outlives the pane's mount), and whether the Notification
 * Center is available (`feat-alerts-center`).
 *
 * It also carries the one hand-off into the pane from outside it: the alert a
 * record page's banner asked the assistant to open. That cannot live on the
 * pane, because below `xl` the pane is not mounted when the click happens.
 *
 * State is in-memory and starts open on the assistant with the card
 * truncated, so the viewer sees the thread on load. It resets on reload
 * (the app has no persisted-UI-state pattern).
 *
 * Consumed by `HomeScene` (renders the column + reflows content),
 * `MantleAssistantNavButton` and `MainSidebar` (toggle it, reflect it),
 * `MainApp`'s `AlertsRouteGate` (hands a deep link off to it), and the pane's
 * `useComposerAlertsCenter` (renders and moves the card) via
 * {@link useSidePanel}; and by `EntityPaneBody` via the narrower
 * {@link useContentColumnsBreakpoint}, so a generic layout primitive depends
 * only on "which breakpoint do my columns use".
 */
const SidePanelProvider = ({ children }: ISidePanelProviderProps) => {
  const isAlertsEnabled = useFeatureGate(FeatureGate.AlertsCenter);
  const [state, dispatch] = React.useReducer(reduceSidePanel, INITIAL_SIDE_PANEL_STATE);
  // Deliberately outside `ISidePanelState`: that shape is what the layout
  // derivations above are typed against, and an alert waiting to be opened
  // decides nothing about the column.
  const [pendingAlertExpand, setPendingAlertExpand] = React.useState<IPendingAlertExpand | null>(null);

  const toggleAssistant = React.useCallback(() => dispatch({ type: 'toggleAssistant' }), []);
  const showAssistant = React.useCallback(() => dispatch({ type: 'showAssistant' }), []);
  const showAlerts = React.useCallback(() => dispatch({ type: 'showAlerts' }), []);
  const toggleAlertsCenter = React.useCallback(() => dispatch({ type: 'toggleAlertsCenter' }), []);
  const expandAlertsCenter = React.useCallback(() => dispatch({ type: 'alerts', action: 'expand' }), []);
  const collapseAlertsCenter = React.useCallback(() => dispatch({ type: 'alerts', action: 'collapse' }), []);
  const viewAllAlertsCenter = React.useCallback(() => dispatch({ type: 'alerts', action: 'viewAll' }), []);
  const openAlertsCenter = React.useCallback(() => dispatch({ type: 'alerts', action: 'open' }), []);
  const closeAlertsCenter = React.useCallback(() => dispatch({ type: 'alerts', action: 'close' }), []);
  const noteAlertExpanded = React.useCallback(() => dispatch({ type: 'alerts', action: 'alertExpanded' }), []);
  const noteUserSent = React.useCallback(() => dispatch({ type: 'alerts', action: 'userSent' }), []);
  const noteRowFixRan = React.useCallback(() => dispatch({ type: 'alerts', action: 'rowFixRan' }), []);

  const openAlertInAssistant = React.useCallback((alertId: string, wasUnread: boolean) => {
    dispatch({ type: 'showAssistant' });
    setPendingAlertExpand({ alertId, wasUnread });
  }, []);

  const consumePendingExpand = React.useCallback(() => setPendingAlertExpand(null), []);

  const visible: SidePanelContent | null = state.isOpen ? 'assistant' : null;

  // Presence tracks the column the user sees: `renderValue` retains the
  // occupant through the exit transition. Gated on `xl` here (not just in the
  // scene) so a downward crossing drops it instantly — the exit animation must
  // never hold `AssistantPane` mounted into the window where the `/assistant`
  // route could mount its WebSocket a second time.
  const isXl = useBreakpoint('xl');
  const panelPresence = useTransitionedPresence(isXl ? visible : null, {
    transitionProperty: 'width',
    durationMs: SIDE_PANEL_TRANSITION_MS,
  });
  const columnOccupiesWidth = isXl && panelPresence.renderValue !== null;

  const value = React.useMemo<ISidePanelContextValue>(() => {
    return {
      isOpen: state.isOpen,
      isAlertsEnabled,
      visible,
      panelPresence,
      // Keyed on presence, not `visible`: panes reflow when the column actually
      // leaves (or enters) the row, not at the logical toggle a half-second
      // before the width has finished (or started) moving.
      columnsBreakpoint: deriveContentColumnsBreakpoint(columnOccupiesWidth),
      toggleAssistant,
      showAssistant,
      showAlerts,
      alertsPresentation: state.alerts.presentation,
      expandAlertsCenter,
      collapseAlertsCenter,
      viewAllAlertsCenter,
      openAlertsCenter,
      closeAlertsCenter,
      toggleAlertsCenter,
      noteAlertExpanded,
      noteUserSent,
      noteRowFixRan,
      pendingAlertExpand,
      openAlertInAssistant,
      consumePendingExpand,
    };
  }, [
    state,
    isAlertsEnabled,
    visible,
    panelPresence,
    columnOccupiesWidth,
    toggleAssistant,
    showAssistant,
    showAlerts,
    expandAlertsCenter,
    collapseAlertsCenter,
    viewAllAlertsCenter,
    openAlertsCenter,
    closeAlertsCenter,
    toggleAlertsCenter,
    noteAlertExpanded,
    noteUserSent,
    noteRowFixRan,
    pendingAlertExpand,
    openAlertInAssistant,
    consumePendingExpand,
  ]);

  return <SidePanelContext.Provider value={value}>{children}</SidePanelContext.Provider>;
};

export default SidePanelProvider;

/**
 * Reads the side-panel state. Used by the shell (`HomeScene`, to render and
 * place the column), the nav toggle button, the bell, the alerts route gate,
 * and the pane's composer card. Outside the provider it returns the closed,
 * gate-off default, so the no-op value is safe in Storybook.
 *
 * @returns The full context value — `isOpen`, `visible`, the alerts gate flag,
 *   `columnsBreakpoint`, the column setters, the card's presentation and its
 *   movers, and the pending-expand hand-off.
 */
export const useSidePanel = (): ISidePanelContextValue => React.useContext(SidePanelContext);

/**
 * Narrow read of just the column breakpoint, for `EntityPaneBody`. Layout-
 * neutral by name so a generic layout primitive depends on "which breakpoint
 * do my columns use," not on what happens to be in the panel.
 *
 * @returns `'2xl'` while the panel occupies content width, otherwise `'xl'`.
 */
export const useContentColumnsBreakpoint = (): ContentColumnsBreakpoint =>
  React.useContext(SidePanelContext).columnsBreakpoint;
